import React, { useState } from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { tasksApi, taskAssignmentsApi, taskOccurrencesApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSAttachmentPreview } from '../../components/ILMSAttachmentPreview';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSSelect } from '../../components/ILMSForm';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { formatDate, formatDateTime, shortId, today } from '../../utils/format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import type { Task, TaskAssignment, TaskOccurrence } from '../../types';
import type { ModuleConfig } from '../../features/common/types';
import { useReferenceData } from '../../features/common/referenceData';

// Page-vs-tab call: Tasks + Task_Assignments are one WF-11 workflow
// (assign -> start -> complete -> verify) so they're tabs on one page.

const isNewTask = (v: Record<string, unknown>) => v.id === undefined;
const isRepeating = (v: Record<string, unknown>) => !!v.repeat && v.repeat !== 'Once';

const REPEAT_OPTIONS = [
  { value: 'Once', label: 'Once (on the date)' },
  { value: 'Daily', label: 'Every day' },
  { value: 'EveryNDays', label: 'Every few days (e.g. every 2 days)' },
  { value: 'Weekly', label: 'Once a week' },
  { value: 'Monthly', label: 'Once a month' },
];
const SHIFT_OPTIONS = [
  { value: 'Morning', label: 'AM (morning)' },
  { value: 'Evening', label: 'PM (evening)' },
  { value: 'Both', label: 'AM + PM (two jobs a day)' },
  { value: 'Anytime', label: 'Any time of day' },
];
const SHIFT_LABEL: Record<string, string> = { Morning: 'AM', Evening: 'PM', Both: 'AM + PM', Anytime: 'Any time' };

/** Blank selects as null, numbers as numbers; a one-off task carries no schedule. */
function taskBody(v: Record<string, any>): Partial<Task> {
  const blank = (x: any) => (x === '' || x === undefined ? null : x);
  const repeat = blank(v.repeat);
  return {
    ...v,
    repeat,
    repeat_every: repeat === 'EveryNDays' ? Number(v.repeat_every) : null,
    repeat_until: repeat && repeat !== 'Once' ? blank(v.repeat_until) : null,
    shifts: repeat ? blank(v.shifts) ?? 'Anytime' : null,
    proof_photos: repeat ? Number(v.proof_photos || 0) : 0,
    proof_source: v.proof_source || 'camera',
    due_date: blank(v.due_date),
  };
}

function repeatLabel(t: Task): string {
  switch (t.repeat) {
    case 'Once': return 'Once';
    case 'Daily': return 'Daily';
    case 'EveryNDays': return `Every ${t.repeat_every ?? '?'} days`;
    case 'Weekly': return 'Weekly';
    case 'Monthly': return 'Monthly';
    default: return '—';
  }
}

const tasksConfig: ModuleConfig<Task> = {
  title: 'Tasks', entityLabel: 'task', module: 'Tasks', queryKey: 'tasks', idKey: 'id',
  api: {
    ...tasksApi,
    // A task only reaches someone's "My Tasks" once it's assigned - so the
    // create form can assign it in the same step (UI-only assign_to field).
    create: async (v: any) => {
      const { assign_to: assignTo, ...task } = v;
      const created = await tasksApi.create(taskBody(task));
      if (assignTo) await taskAssignmentsApi.create({ task_id: created.id, assigned_to: assignTo } as any);
      return created;
    },
    update: (id: string, v: any) => tasksApi.update(id, taskBody(v)),
  },
  referenceKeys: ['farms', 'users'],
  defaultValues: { priority: 'Medium', status: 'Pending', repeat: '' as any, proof_photos: '0' as any, proof_source: 'camera' },
  deriveEditValues: (r) => ({ repeat: r.repeat ?? '', shifts: r.shifts ?? '', proof_photos: String(r.proof_photos ?? 0) }),
  columns: [
    { key: 'title', header: 'Title', primary: true, render: (r) => r.title },
    { key: 'type', header: 'Type', render: (r) => r.task_type },
    { key: 'repeat', header: 'Repeats', render: (r) => repeatLabel(r) },
    { key: 'shifts', header: 'Shift', hideOnTablet: true, render: (r) => SHIFT_LABEL[r.shifts ?? ''] ?? '—' },
    { key: 'proof', header: 'Photo proof', hideOnTablet: true, render: (r) => (r.proof_photos ? `${r.proof_photos} · ${r.proof_source === 'any' ? 'camera/gallery' : 'live camera'}` : '—') },
    { key: 'due', header: 'Due / From', render: (r) => r.due_date || '—' },
    { key: 'priority', header: 'Priority', render: (r) => <ILMSStatusBadge status={r.priority} size="sm" /> },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Task', required: true, optionsKey: 'farms' },
    { name: 'task_type', label: 'Type', type: 'select', section: 'Task', required: true, staticOptions: [
      { value: 'Milking', label: 'Milking' }, { value: 'Feeding', label: 'Feeding' }, { value: 'Cleaning', label: 'Cleaning' },
      { value: 'Vaccination', label: 'Vaccination' }, { value: 'Health', label: 'Health' }, { value: 'Utility', label: 'Utility' },
      { value: 'Other', label: 'Other' },
    ] },
    { name: 'title', label: 'Title', type: 'text', section: 'Task', required: true },
    { name: 'assign_to' as any, label: 'Assign To', type: 'select', section: 'Task', optional: true, optionsKey: 'users', visibleWhen: isNewTask,
      helperText: 'Shows straight away under "My Tasks" in their mobile app. Leave blank to assign later from the Assignments tab.' },
    { name: 'description', label: 'Description', type: 'textarea', section: 'Task', optional: true, fullWidth: true },
    // Repeating work lands on the agent's task list by itself, each day/shift.
    { name: 'repeat', label: 'Repeat', type: 'select', section: 'Schedule', optional: true, staticOptions: REPEAT_OPTIONS,
      helperText: 'Blank = a one-off task. Repeating tasks appear on the agent\'s list by themselves, every time they are due.' },
    { name: 'repeat_every', label: 'Every how many days', type: 'number', section: 'Schedule', min: 1,
      visibleWhen: (v) => v.repeat === 'EveryNDays', required: (v) => v.repeat === 'EveryNDays' },
    { name: 'due_date', label: 'Due Date / Starts on', type: 'date', section: 'Schedule',
      required: (v) => !!v.repeat && v.repeat !== 'Once',
      helperText: 'For a repeating task: the first day. Weekly repeats on this weekday, monthly on this date.' },
    { name: 'repeat_until', label: 'Ends on (optional)', type: 'date', section: 'Schedule', optional: true, visibleWhen: isRepeating },
    { name: 'shifts', label: 'Shift', type: 'select', section: 'Schedule', optional: true, visibleWhen: (v) => !!v.repeat, staticOptions: SHIFT_OPTIONS,
      helperText: 'AM + PM makes two jobs a day (e.g. shed cleaning morning and evening).' },
    { name: 'proof_photos', label: 'Photo proof needed', type: 'select', section: 'Proof', visibleWhen: (v) => !!v.repeat, staticOptions: [
      { value: '0', label: 'None' }, { value: '1', label: '1 photo' }, { value: '2', label: '2 photos' },
    ] },
    { name: 'proof_source', label: 'Photo from', type: 'select', section: 'Proof', visibleWhen: (v) => !!v.repeat && Number(v.proof_photos) > 0, staticOptions: [
      { value: 'camera', label: 'Live camera only (no gallery uploads)' }, { value: 'any', label: 'Camera or gallery' },
    ] },
    { name: 'priority', label: 'Priority', type: 'select', section: 'Schedule', required: true, staticOptions: [
      { value: 'Low', label: 'Low' }, { value: 'Medium', label: 'Medium' }, { value: 'High', label: 'High' }, { value: 'Critical', label: 'Critical' },
    ] },
    { name: 'status', label: 'Status', type: 'select', section: 'Schedule', required: true, staticOptions: [
      { value: 'Pending', label: 'Pending' }, { value: 'InProgress', label: 'In Progress' }, { value: 'Completed', label: 'Completed' },
      { value: 'Verified', label: 'Verified' }, { value: 'Cancelled', label: 'Cancelled' },
    ] },
  ],
};

function TaskAssignmentsTab() {
  const queryClient = useQueryClient();
  // Show task titles and usernames, not id fragments.
  const { data: ref } = useReferenceData(['users', 'tasks']);
  const userName = (id: string) => ref.users.find((u) => u.value === id)?.label ?? shortId(id);
  const taskTitle = (id: string) => ref.tasks.find((t) => t.value === id)?.label ?? shortId(id);
  const config: ModuleConfig<TaskAssignment> = {
    title: 'Task Assignments', entityLabel: 'assignment', module: 'Tasks', queryKey: 'task-assignments',
    api: taskAssignmentsApi, idKey: 'id', referenceKeys: ['users', 'tasks'],
    addLabel: 'Assign Task',
    columns: [
      { key: 'task', header: 'Task', primary: true, render: (r) => taskTitle(r.task_id) },
      { key: 'assigned_to', header: 'Assigned To', render: (r) => userName(r.assigned_to) },
      { key: 'assigned_at', header: 'Assigned At', render: (r) => formatDateTime(r.assigned_at), hideOnTablet: true },
      {
        key: 'progress', header: 'Progress',
        render: (r) => {
          const label = r.verified_at ? 'Verified' : r.completed_at ? 'Completed' : r.started_at ? 'InProgress' : 'Pending';
          return <ILMSStatusBadge status={label} size="sm" />;
        },
      },
      {
        key: 'actions', header: 'Actions',
        render: (r) => (
          <div className="flex gap-1.5">
            {!r.started_at && (
              <ILMSButton size="sm" variant="outline" onClick={() => taskAssignmentsApi.start(r.id).then(() => queryClient.invalidateQueries({ queryKey: ['task-assignments'] }))}>Start</ILMSButton>
            )}
            {r.started_at && !r.completed_at && (
              <ILMSButton size="sm" variant="outline" onClick={() => taskAssignmentsApi.complete(r.id).then(() => queryClient.invalidateQueries({ queryKey: ['task-assignments'] }))}>Complete</ILMSButton>
            )}
            {r.completed_at && !r.verified_at && (
              <ILMSButton size="sm" variant="outline" onClick={() => taskAssignmentsApi.verify(r.id).then(() => queryClient.invalidateQueries({ queryKey: ['task-assignments'] }))}>Verify</ILMSButton>
            )}
          </div>
        ),
      },
    ],
    fields: [
      { name: 'task_id', label: 'Task', type: 'select', section: 'Assignment', required: true, optionsKey: 'tasks' },
      { name: 'assigned_to', label: 'Assign To', type: 'select', section: 'Assignment', required: true, optionsKey: 'users' },
    ],
  };
  return <ResourceModule config={config} />;
}

const STATE_TONE: Record<TaskOccurrence['state'], 'success' | 'warning' | 'critical' | 'info' | 'neutral'> = {
  Done: 'success', 'To do': 'warning', Later: 'neutral', Locked: 'critical', 'Reopen requested': 'warning', Reopened: 'info',
};

// Recurring work, day by day: who did which job, when, with which photos -
// and the locked ones (24h after their shift) an Admin can reopen.
function DailyLogTab() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canReopen = can('Tasks', 'Approve');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [state, setState] = useState('');
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['task-occurrences', from, to, state],
    queryFn: () => taskOccurrencesApi.list({ from_date: from, to_date: to, state: state || undefined }),
  });
  const reopen = useMutation({
    mutationFn: (id: string) => taskOccurrencesApi.reopen(id),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ['task-occurrences'] }); },
    onError: (err) => setError(getApiErrorMessage(err)),
  });
  const rows = query.data ?? [];
  const waiting = rows.filter((r) => r.state === 'Reopen requested').length;

  const columns: ILMSTableColumn<TaskOccurrence>[] = [
    { key: 'date', header: 'Date', render: (r) => formatDate(r.due_date) },
    { key: 'shift', header: 'Shift', render: (r) => SHIFT_LABEL[r.shift] ?? r.shift },
    { key: 'task', header: 'Task', primary: true, render: (r) => r.title },
    { key: 'who', header: 'Assigned to', render: (r) => r.assignee_name },
    { key: 'state', header: 'Status', render: (r) => <ILMSStatusBadge status={r.state} tone={STATE_TONE[r.state]} size="sm" /> },
    { key: 'done', header: 'Done at', render: (r) => (r.completed_at ? formatDateTime(r.completed_at) : `locks ${formatDateTime(r.lock_at)}`) },
    {
      key: 'photos', header: 'Photos', sortable: false,
      render: (r) => (r.photo_ids.length
        ? <div className="flex gap-2">{r.photo_ids.map((id) => <ILMSAttachmentPreview key={id} attachmentId={id} label="" />)}</div>
        : r.proof_photos ? `0 of ${r.proof_photos}` : '—'),
    },
    {
      key: 'note', header: 'Note', hideOnTablet: true,
      render: (r) => r.reopen_reason && r.state === 'Reopen requested' ? `Asked: ${r.reopen_reason}` : r.remarks || '—',
    },
    {
      key: 'action', header: '', sortable: false,
      render: (r) => canReopen && (r.state === 'Locked' || r.state === 'Reopen requested') ? (
        <ILMSButton size="sm" variant="outline" loading={reopen.isPending && reopen.variables === r.id} onClick={() => reopen.mutate(r.id)}>
          Reopen 24h
        </ILMSButton>
      ) : null,
    },
  ];

  return (
    <ILMSCard>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <ILMSDatePicker label="From" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <ILMSDatePicker label="To" value={to} max={today()} onChange={(e) => setTo(e.target.value)} />
        <label className="flex flex-col gap-1 text-label text-ink-700">
          Status
          <ILMSSelect value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All</option>
            {Object.keys(STATE_TONE).map((s) => <option key={s} value={s}>{s}</option>)}
          </ILMSSelect>
        </label>
        {waiting > 0 && <p className="text-secondary font-medium text-warning-700 pb-2">{waiting} reopen request{waiting > 1 ? 's' : ''} waiting</p>}
      </div>
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
      <ILMSTable
        columns={columns} data={rows} rowKey={(r) => r.id}
        exportTitle="Repeating Jobs"
        isLoading={query.isLoading} error={query.isError ? getApiErrorMessage(query.error) : null} onRetry={() => query.refetch()}
        emptyTitle="No repeating jobs on these days."
        emptyDescription="Set a task to repeat (Tasks → edit → Repeat) and assign it - each day's jobs show here."
      />
    </ILMSCard>
  );
}

export default function TasksPage() {
  return (
    <TabbedPage
      title="Tasks"
      subtitle="One-off and repeating work (daily, weekly…, AM/PM, with photo proof) — assigned jobs reach the agent's app by themselves."
      tabs={[
        { id: 'tasks', label: 'Tasks', render: () => <ResourceModule config={tasksConfig} /> },
        { id: 'daily-log', label: 'Daily Log', render: () => <DailyLogTab /> },
        { id: 'assignments', label: 'Assignments', render: () => <TaskAssignmentsTab /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Tasks', tasksConfig);
